import * as React from "react";
import { useInterval } from "usehooks-ts";
import Result from "antd/es/result";
import Progress from "antd/es/progress";
import { useExchange } from "../api";
import { Chains, FormModel } from "./types";
import { secondsLimit } from "./constants";
import { ErrorDisplay } from "./ErrorDisplay";
import ReactScrollIntoViewIfNeededInternal from "react-scroll-into-view-if-needed";

const ReactScrollIntoViewIfNeeded = ReactScrollIntoViewIfNeededInternal as any as React.FunctionComponent<React.PropsWithChildren>;

export interface PaymentManagerProps {
    formModel: FormModel;
    onSuccess: () => void;
}

const singleQueryChains: Chains[] = [
    "Ethereum",
    "Tron",
    "Solana",
];

export const PaymentManager: React.FunctionComponent<PaymentManagerProps> = (props) => {
    const isSingleQueryChain = singleQueryChains.includes(props.formModel.fromChain);
    const shouldQuery = React.useMemo(() => {
        if (isSingleQueryChain && !props.formModel.transactionHash) {
            return false;
        }
        return true;
    }, [isSingleQueryChain, props.formModel]);
    const [seconds, setSeconds] = React.useState(secondsLimit);
    const exchange = useExchange();
    const isSuccess = exchange.isSuccess && exchange.data;
    useInterval(() => {
        if (!exchange.isPending) {
            setSeconds(!seconds ? secondsLimit : seconds - 1);
        }
    }, 1000);
    
    React.useEffect(() => {
        if (seconds === 0 && shouldQuery && !exchange.isPending && !exchange.isError) {
            exchange.mutate(props.formModel);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [seconds, shouldQuery]);

    React.useEffect(() => {
        if (isSuccess) {
            props.onSuccess();
        }
    }, [isSuccess, props]);

    if (!shouldQuery) {
        return null;
    }

    if (exchange.isError) {
        return (
            <ErrorDisplay
                orderId={props.formModel.orderId}
                retry={{
                    label: "Retry payment confirmation",
                    method: () => exchange.reset(),
                }}
            />
        );
    }

    const result = (
        <Result
            title="Processing payment"
            className="ProcessingPayment"
            extra={(
                <Progress
                    percent={(100 / secondsLimit) * (secondsLimit - seconds)}
                    status={exchange.isError ? "exception" : exchange.isSuccess ? "success" : "normal"}
                    format={(percent) => {
                        if (exchange.isPending) {
                            return "Refreshing";
                        }
                        if (exchange.isError) {
                            return "Error!";
                        }
                        if (exchange.isSuccess && exchange.data) {
                            return "Done!";
                        }
                        if (percent === 100) {
                            return "Refreshing";
                        }
                        return `Refreshing in ${seconds}s`;
                    }}
                />
            )}
        />
    );

    if (isSingleQueryChain) {
        return (
            <ReactScrollIntoViewIfNeeded>
                {result}
            </ReactScrollIntoViewIfNeeded>
        )
    }

    return result;
};